import React, {useCallback, useEffect, useState} from "react";
import {useHistory} from "react-router";
import {useLocation} from "react-router-dom";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import {Button, Card, Col, Container, Row} from "react-bootstrap";
import {withAuthorizationVerifier} from "../../components/authorization-verifier";
import {SectionHeading, SectionSubHeading} from "../../components/section-heading";
import LoyaltyCurrentTierInformation from "./components/LoyaltyCurrentTierInformation";
import LoyaltyCustomerTierLevels from "./components/LoyaltyCustomerTierLevels";
import ContentWrapper from "../../components/content-wrapper";
import {
    getCustomerTierInfo,
    GetCustomerTierInfo,
    calculateCustomerTier,
    getCustomer,
    GetCustomer,
    upgradeCustomerTierLevel
} from "../shared/api/loyaltyApi";
import {AlertWrapper} from "./wrappers/AlertWrapper";
import LoyaltyCustomerTierUpgradeForm from "./components/LoyaltyCustomerTierUpgradeForm";
import {ConfirmModal, ConfirmModalContentProps} from "./components/ConfirmModal";
import {StyledBackButton, StyledSpinner, VerticalLine} from "./styles";

const messages = defineMessages({
    entityName: {
        id: "loyaltyCustomerHistorical.title",
        defaultMessage: "Upgrade loyalty tier",
    },
    subHeader: {
        id: "loyaltyCustomerHistorical.levelEditCustomer",
        defaultMessage: "Level Update - User: {customerEmailAddress}",
    },
    save: {
        id: "default.save",
        defaultMessage: "Save",
    },
    back: {
        id: "default.goBack",
        defaultMessage: "Back",
    },
    getCustomerInfoError: {
        id: "loyaltyCustomerHistorical.getCustomerInfoError",
        defaultMessage: "Error on getting customer tier information",
    },
    getCustomerError: {
        id: "loyaltyCustomerHistorical.getCustomerError",
        defaultMessage: "Error on getting customer",
    },
    upgradeCustomerTierLevelError: {
        id: "loyaltyCustomerHistorical.upgradeCustomerTierLevelError",
        defaultMessage: "Error on upgrading customer tier level",
    },
    upgradeCustomerTierLevelSuccess: {
        id: "loyaltyCustomerHistorical.upgradeCustomerTierLevelSuccess",
        defaultMessage: "Customer has been updated successfully",
    }
});

export interface TierLevelUpgradeTransactionAmount {
    transactionNumber: number;
    spentAmount: {
        amount: number;
        currency: string;
    }
}

const initialModalContent: ConfirmModalContentProps = {
    currentLevel: "",
    newLevel: "",
    amount: 0,
    currency: "EUR",
    transactionNumber: 0,
    expirationDate: ""
};

const initialTransactionAmount = {
    transactionNumber: 0,
    spentAmount: {
        amount: 0,
        currency: "EUR"
    }
};

function LoyaltyHistorical() {
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const customerId = location.pathname.split("/").pop();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<{ id: string, defaultMessage: string } | null>(null);
    const [success, setSuccess] = useState<{ id: string, defaultMessage: string } | null>(null);
    const [isInvalidForm, setIsInvalidForm] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<ConfirmModalContentProps>(initialModalContent);
    const [customer, setCustomer] = useState<GetCustomer>();
    const [customerTierInfo, setCustomerTierInfo] = useState<GetCustomerTierInfo>();
    const [transactionAmount, setTransactionAmount] = useState<TierLevelUpgradeTransactionAmount>(initialTransactionAmount);

    useEffect(() => {
        const asyncFun = async () => {
            const response = await getCustomer(customerId!);
            setCustomer(response);
        };
        asyncFun()
            .catch(() => setError(messages.getCustomerError))
            .finally(() => setLoading(false));
    }, [customerId])

    useEffect(() => {
        const asyncFun = async () => {
            const response = await getCustomerTierInfo(customerId!);
            setCustomerTierInfo(response);
        };
        asyncFun()
            .catch(() => setError(messages.getCustomerInfoError))
            .finally(() => setLoading(false));
    },[customerId]);

    const handleOnBack = useCallback(() => history.goBack(), [history]);

    const handleOnConfirmModal = useCallback(async(info: ConfirmModalContentProps) => {
        try {
            await upgradeCustomerTierLevel(customerId!, customerTierInfo!.tierConditionType, transactionAmount);
            setCustomerTierInfo({
                ...customerTierInfo!,
                info: {
                    transactionsNumber: {
                        total: info.transactionNumber + (customerTierInfo?.info?.transactionsNumber?.total || 0),
                        validated: customerTierInfo?.info?.transactionsNumber?.validated || 0,
                        pending: info.transactionNumber + (customerTierInfo?.info?.transactionsNumber?.pending || 0),
                    },
                    spentAmount: {
                        total: {
                            currencyCode: info.currency,
                            units: info.amount + (customerTierInfo?.info?.spentAmount?.total?.units || 0),
                            nanos: 0,
                        },
                        validated: {
                            currencyCode: info.currency,
                            units: customerTierInfo?.info?.spentAmount?.validated?.units || 0,
                            nanos: 0,
                        },
                        pending: {
                            currencyCode: info.currency,
                            units: info.amount + (customerTierInfo?.info?.spentAmount?.pending?.units || 0),
                            nanos: 0,
                        },
                    }
                }
            });
            setSuccess(messages.upgradeCustomerTierLevelSuccess)
            setModalOpen(false);
        } catch (e) {
            setError(messages.upgradeCustomerTierLevelError);
        }
    }, [customerId, customerTierInfo, transactionAmount]);

    const handleOnSave = useCallback(async () => {
        const response = await calculateCustomerTier(
            customerId!,
            customerTierInfo!.tierConditionType,
            transactionAmount
        );
        setModalContent({
            currentLevel: customerTierInfo!.tierTicker,
            newLevel: response.tierTicker,
            expirationDate: response.expiresAt,
            transactionNumber: transactionAmount.transactionNumber || 0,
            amount: transactionAmount.spentAmount.amount || 0,
            currency: transactionAmount.spentAmount.currency || "EUR"
        });
        setModalOpen(true);
    }, [customerId, customerTierInfo, transactionAmount]);

    return (
        <ContentWrapper>
            <SectionHeading>
                <FormattedMessage {...messages.entityName} />
            </SectionHeading>
            {customer && (
                <SectionSubHeading>
                    {intl.formatMessage(messages.subHeader, { customerEmailAddress: customer.emailAddress })}
                </SectionSubHeading>
            )}
            <AlertWrapper error={error} success={success}>
                <Card className="ml-4 mr-4 mb-4 mt-4">
                    <Card.Body>
                        {loading && (
                            <Container className="text-center text-sm-center">
                                <StyledSpinner className="fa fa-spinner fa-spin" />
                            </Container>
                        )}
                        {!loading && customerTierInfo && (
                            <>
                                <Container fluid>
                                    <Row>
                                        <Col xs={12} sm={12} md={12} lg={6} xl={4}>
                                            <LoyaltyCurrentTierInformation data={customerTierInfo} />
                                        </Col>
                                        <Col xs={12} sm={12} md={3} lg={1} xl={1} className="justify-content-center d-none d-lg-block">
                                            <VerticalLine />
                                        </Col>
                                        <Col xs={12} sm={12} md={12} lg={3} xl={1} className="align-items-start d-block d-lg-none">
                                            <hr/>
                                        </Col>
                                        <Col xs={12} sm={12} md={12} lg={3} xl={3}>
                                            <LoyaltyCustomerTierUpgradeForm
                                                tierConditionType={customerTierInfo.tierConditionType}
                                                transactionAmount={transactionAmount}
                                                setTransactionAmount={setTransactionAmount}
                                                isInvalid={(isInvalid: boolean) => setIsInvalidForm(isInvalid)}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} sm={12} md={12} lg={8} xl={5}>
                                            <LoyaltyCustomerTierLevels levels={customerTierInfo.levelsList} />
                                        </Col>
                                    </Row>
                                </Container>
                                <Button
                                    variant="primary"
                                    className="mt-3 mr-3"
                                    disabled={isInvalidForm}
                                    onClick={handleOnSave}>
                                    {intl.formatMessage(messages.save)}
                                </Button>
                                <StyledBackButton className="mt-3" onClick={handleOnBack}>
                                    {intl.formatMessage(messages.back)}
                                </StyledBackButton>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </AlertWrapper>
            <ConfirmModal
                isOpen={modalOpen}
                handleOnHideModal={() => setModalOpen(false)}
                handleOnConfirmModal={() => handleOnConfirmModal(modalContent)}
                content={modalContent}
            />
        </ContentWrapper>
    );
}

export default withAuthorizationVerifier(LoyaltyHistorical);